import {getTestProps} from '@benchling/js-test-utils';
import {Box, Group, GroupProps, packSx, Stack, StackProps, Text} from '@mantine/core';
import React, {PropsWithChildren, ReactNode} from 'react';

import {FONT_WEIGHT} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export type SectionRowProps = PropsWithChildren<GroupProps>;

/**
 * A row within a {@link BenchlingNode.Section}. This is a flex row with BenchlingNode styling applied.
 *
 * @package
 */
export function SectionRow(props: SectionRowProps): JSX.Element {
  return <Group px="sm" pos="relative" m="xs" {...props} />;
}

export interface SectionProps {
  /**
   * The left header of the section. Text is displayed in small, black, semibold at the top left of the section.
   */
  leftHeader: string;

  /**
   * The right header of the section.
   */
  rightHeader?: string | ReactNode;
}

/**
 * A section within a {@link BenchlingNode.Sections}. This is a flex column with BenchlingNode styling
 * applied.
 *
 * This component is a context consumer. As such, it should be rendered as a child of a
 * {@link BenchlingNode}.
 *
 * @package
 */
export function Section(props: SectionProps & PropsWithChildren<StackProps>): JSX.Element {
  const {leftHeader, rightHeader, children, sx, ...stackProps} = props;

  return (
    <Stack
      spacing="0px"
      sx={[
        (theme) => ({
          '&:nth-of-type(n)': {
            borderTop: `1px solid ${theme.colors.gray[3]}`,
          },
          '&:first-of-type': {
            borderTopWidth: '0px',
          },
          '&:last-of-type': {
            borderBottomLeftRadius: theme.radius.md,
            borderBottomRightRadius: theme.radius.md,
          },
        }),
        ...packSx(sx),
      ]}
      {...stackProps}
      {...getTestProps({component: 'Section', element: 'root', key: leftHeader})}
    >
      <Group spacing={0}>
        <Text c="gray.9" fz="xs" fw={FONT_WEIGHT.SEMIBOLD} mt="xs" pl="sm" py={0}>
          {leftHeader}
        </Text>
        {rightHeader && <Box>{rightHeader}</Box>}
      </Group>
      {children}
    </Stack>
  );
}

export type SectionsProps = PropsWithChildren<StackProps>;

/**
 * A wrapper for one or more {@link BenchlingNode.Section}.
 *
 * @package
 */
export function Sections(props: SectionsProps): JSX.Element {
  const {children, ...stackProps} = props;
  return (
    <Stack spacing="xs" pb="xs" {...stackProps}>
      {props.children}
    </Stack>
  );
}
